import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useFormState } from 'react-final-form';
import fastDeepEqual from 'fast-deep-equal';
import { useParams } from 'react-router';
import { useMediaQuery } from '@mui/material';

import TooltipButton from '../../../../../components/tooltip-button/TooltipButton';
import { BUTTON_STYLES } from '../../../../../components/common-button/CommonButton';
import { SCOUT_TABS } from '../../../../../constants';
import ResetSessionModal from '../reset-session-modal/ResetSessionModal';

import scss from './GenerateButton.module.scss';

const GenerateButton = ({ onSubmit, onResetSession }) => {
  const { t } = useTranslation('scout');
  const dispatch = useDispatch();
  const { id } = useParams();

  const isTablet = useMediaQuery('(max-width: 1024px)');

  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  const { values, initialValues, dirty } = useFormState();

  const isFormLoading = useSelector((state) => state.scout.isFormLoading);
  const isGenerating = useSelector((state) => state.scout.isGenerating);
  const scoutTab = useSelector((state) => state.scout.scoutTab);
  const channels = useSelector((state) => state.scout.generationForm.channels);

  const isShouldResetTheForm = useMemo(() => {
    const isSomeAssistantHasHint = Object.values(channels).some((channel) => channel.hint);
    if (!isSomeAssistantHasHint) {
      return false;
    }

    const changedUrl = (values.url || '') !== (initialValues.url || '');

    const changedFileText = (values.fileText || '') !== (initialValues.fileText || '');

    const initialAssistants = Object.values(initialValues.channels).map((channel) => channel.assistant.value);
    const changedChannels = Object.values(values.channels).map((channel) => channel.assistant.value);

    const isAssistantChanged = !fastDeepEqual(changedChannels, initialAssistants);

    return changedUrl || changedFileText || isAssistantChanged;
  }, [values, initialValues, channels]);

  const { isDisabled, tooltipText } = useMemo(() => {
    if (scoutTab === SCOUT_TABS.ALL_MESSAGES && !dirty) {
      return { isDisabled: true, tooltipText: t('fillChangeTheFormToGenerateMessages') };
    }

    if (isGenerating) {
      return { isDisabled: true, tooltipText: t('messagesGenerating') };
    }

    if (isFormLoading) {
      return { isDisabled: true, tooltipText: undefined };
    }

    return { isDisabled: false, tooltipText: undefined };
  }, [isFormLoading, isGenerating, dirty, scoutTab, t]);

  const mapButtonText = {
    [SCOUT_TABS.ALL_MESSAGES]: t('generate'),
    [SCOUT_TABS.SELECTED_MESSAGES]: t('generateMore'),
    [SCOUT_TABS.FINAL_MESSAGES]: t('compressMessages'),
  };

  const handleCancelGeneration = () => {
    setIsResetModalOpen(false);
  };

  const handleSubmitGeneration = () => {
    setIsResetModalOpen(false);

    onSubmit();
  };

  const handleResetAndSubmit = async () => {
    setIsResetModalOpen(false);

    await dispatch(onResetSession(id));

    onSubmit();
  };

  const handleSubmit = (e) => {
    if (isShouldResetTheForm) {
      e.preventDefault();
      setIsResetModalOpen(true);
      return;
    }

    onSubmit();
  };

  if (scoutTab === SCOUT_TABS.SCREENED_MESSAGES || scoutTab === SCOUT_TABS.BACKUP) {
    return null;
  }

  return (
    <>
      <TooltipButton
        styleColor={BUTTON_STYLES.PRIMARY}
        type='button'
        maxWidth={isTablet ? undefined : 160}
        isLoading={isGenerating}
        disabled={isDisabled}
        tooltip={tooltipText}
        className={scss.generateButton}
        onClick={handleSubmit}>
        {mapButtonText[scoutTab]}
      </TooltipButton>

      <ResetSessionModal
        isOpen={!!isResetModalOpen}
        onCancel={handleCancelGeneration}
        onChange={handleSubmitGeneration}
        onChangeAndReset={handleResetAndSubmit}
      />
    </>
  );
};

export default GenerateButton;
